import { memo, useState } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import Animated, {
    AnimatedStyle,
    Extrapolation,
    SharedValue,
    interpolate,
    interpolateColor,
    useAnimatedStyle,
} from 'react-native-reanimated';

import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components/Box';
import { Image as FastImage } from '@/components/lib/components/Image';
import { ds } from '@/components/lib/design.system';
import { LinearGradient } from '@/components/linear-gradient/LinearGradient';
import { CardShadow } from '@/feature/lobby/components/race-to-place/CardShadow';
import { designSystem, withOpacity } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';
import { KeyTextField } from '@prismicio/types';

import { WhatsNewCardData } from '../hooks/use-whats-new-data';
import {
    BACK_CARD_OFFSET,
    BACK_CARD_SCALE_FACTOR,
    MIDDLE_CARD_OFFSET,
    MIDDLE_CARD_SCALE_FACTOR,
    TRANSLATE_X_THRESHOLD,
    WHATS_NEW_CARD_HEIGHT,
    WHATS_NEW_CARD_HORIZONTAL_MARGIN,
} from '../utils/whats-new-animations-utils';

type CardContentProps = {
    image?: string | null;
    title?: string | KeyTextField;
    description?: string | KeyTextField;
    style?: AnimatedStyle;
    shadowStyle?: AnimatedStyle;
    renderContent?: boolean;
};

type CardProps = {
    index: number;
    animatedActiveIndex: SharedValue<number>;
    translateX: SharedValue<number>;
    translateY: SharedValue<number>;
    card: WhatsNewCardData;
    renderContent?: boolean;
};

export const WhatsNewCard = memo(
    ({ index, animatedActiveIndex, translateX, translateY, card, renderContent }: CardProps) => {
        const style = useAnimatedStyle(() => {
            // relative position to the front card
            const position = index - animatedActiveIndex.value;

            // If swiped away or too far back, hide it
            if (position < 0 || position > 3) return { opacity: 0, zIndex: -1 };

            if (position === 0) {
                // front card
                return {
                    transform: [
                        { translateX: translateX.value },
                        { translateY: translateY.value },
                        { rotate: `${translateX.value / 10}deg` },
                    ],
                    zIndex: 4,
                };
            }
            if (position === 1) {
                // middle card
                return {
                    transform: [
                        {
                            translateY: interpolate(
                                translateX.value,
                                [-TRANSLATE_X_THRESHOLD, 0, TRANSLATE_X_THRESHOLD],
                                [0, -MIDDLE_CARD_OFFSET, 0],
                                Extrapolation.CLAMP
                            ),
                        },
                        {
                            scale: interpolate(
                                translateX.value,
                                [-TRANSLATE_X_THRESHOLD, 0, TRANSLATE_X_THRESHOLD],
                                [1, MIDDLE_CARD_SCALE_FACTOR, 1],
                                Extrapolation.CLAMP
                            ),
                        },
                    ],
                    backgroundColor: interpolateColor(
                        translateX.value,
                        [-TRANSLATE_X_THRESHOLD, 0, TRANSLATE_X_THRESHOLD],
                        [designSystem.colors.neutral900, designSystem.colors.neutral700, designSystem.colors.neutral900]
                    ),
                    zIndex: 3,
                };
            }
            if (position === 2) {
                // back card
                return {
                    transform: [
                        {
                            translateY: interpolate(
                                translateX.value,
                                [-TRANSLATE_X_THRESHOLD, 0, TRANSLATE_X_THRESHOLD],
                                [-MIDDLE_CARD_OFFSET, -BACK_CARD_OFFSET, -MIDDLE_CARD_OFFSET],
                                Extrapolation.CLAMP
                            ),
                        },
                        {
                            scale: interpolate(
                                translateX.value,
                                [-TRANSLATE_X_THRESHOLD, 0, TRANSLATE_X_THRESHOLD],
                                [MIDDLE_CARD_SCALE_FACTOR, BACK_CARD_SCALE_FACTOR, MIDDLE_CARD_SCALE_FACTOR],
                                Extrapolation.CLAMP
                            ),
                        },
                    ],
                    backgroundColor: ds.colorValue.background.neutral.subtler,
                    zIndex: 2,
                };
            }

            return {
                // last card
                transform: [{ translateY: -BACK_CARD_OFFSET }, { scale: BACK_CARD_SCALE_FACTOR }],
                backgroundColor: ds.colorValue.background.neutral.subtler,
                zIndex: 1,
            };
        });

        const shadowStyle = useAnimatedStyle(() => {
            const position = index - animatedActiveIndex.value;
            if (position === 1) {
                return { opacity: interpolate(translateX.value, [-1, 0, 1], [0, 1, 0], Extrapolation.CLAMP) };
            }
            return { opacity: position === 0 ? 0 : 1 };
        });

        return (
            <WhatsNewCardContent
                style={style}
                shadowStyle={shadowStyle}
                image={card.data.picture.url}
                title={card.data.title}
                description={card.data.text}
                renderContent={renderContent}
            />
        );
    }
);

const WhatsNewCardContent = memo(
    ({ image, title, description, style, shadowStyle, renderContent = true }: CardContentProps) => {
        const [scrollEnabled, setScrollEnabled] = useState(false);
        const width = useWindowDimensions().width;
        const cardWidth = isWeb ? '100%' : width - WHATS_NEW_CARD_HORIZONTAL_MARGIN;
        return (
            <Animated.View style={[styles.card, style, { width: cardWidth }]}>
                <Animated.View style={shadowStyle}>
                    <CardShadow />
                </Animated.View>

                {renderContent ? (
                    <ScrollView
                        showsVerticalScrollIndicator={false}
                        scrollEnabled={scrollEnabled}
                        onContentSizeChange={(_, contentHeight) => {
                            setScrollEnabled(contentHeight > WHATS_NEW_CARD_HEIGHT);
                        }}
                    >
                        <Box gap={'s24'} paddingVertical={'s48'} px={'s24'}>
                            <Box justifyContent={'center'} alignItems={'center'}>
                                <FastImage
                                    source={require('@/assets/images/Whats_new_glow.png')}
                                    resizeMode={'cover'}
                                    style={styles.imageBackgroundGlow}
                                />

                                {image ? (
                                    <FastImage source={{ uri: image }} resizeMode={'cover'} style={styles.image} />
                                ) : (
                                    <Box width={128} height={128} />
                                )}
                            </Box>
                            <Box alignItems={'center'} gap={'s4'}>
                                <Text
                                    variant={'teamLogoFallback'}
                                    lineHeight={32}
                                    fontWeight={600}
                                    mb={'s4'}
                                    color={ds.colorToken.text.primary}
                                    textAlign={'center'}
                                >
                                    {title}
                                </Text>
                                <Text variant={'bodyMedium'} color={ds.colorToken.text.secondary} textAlign={'center'}>
                                    {description}
                                </Text>
                            </Box>
                        </Box>
                    </ScrollView>
                ) : null}
                <LinearGradient
                    colors={GRADIENT_COLORS}
                    start={GRADIENT_START}
                    end={GRADIENT_END}
                    width={width - WHATS_NEW_CARD_HORIZONTAL_MARGIN}
                    height={48}
                    style={styles.gradient}
                />
            </Animated.View>
        );
    }
);

const GRADIENT_COLORS = [
    designSystem.colors.neutral900,
    withOpacity(designSystem.colors.neutral900, 0.9),
    withOpacity(designSystem.colors.neutral900, 0.5),
    withOpacity(designSystem.colors.neutral900, 0),
];

const GRADIENT_START = { x: 0, y: 1 };
const GRADIENT_END = { x: 0, y: 0 };

const styles = StyleSheet.create({
    card: {
        borderWidth: 1,
        borderColor: ds.colorValue.border.strong,
        height: WHATS_NEW_CARD_HEIGHT,
        flex: 1,
        position: 'absolute',
        overflow: 'hidden',
        borderRadius: 40,
        backgroundColor: ds.colorValue.background.neutral.base,
    },
    image: {
        width: 128,
        height: 128,
    },
    imageBackgroundGlow: {
        width: 296,
        height: 296,
        position: 'absolute',
    },
    gradient: {
        position: 'absolute',
        height: 48,
        left: 0,
        right: 0,
        bottom: 0,
    },
});
